import {
  Container,
  InputField,
  OrganizationSelect,
  PasswordField,
  SubmitButton
} from '@/components/Auth/Buttons';
import { PasswordRequirements } from '@/components/Auth/PasswordRequirements';
import { useAuth } from '@/providers/AuthProvider';
import { SignUpInput } from 'aws-amplify/auth';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listOrganizationsPublic } from '@/client/organization';
import { CheckboxField } from '@/components/Auth/CheckboxField';

// Define types for state
type SignUpInputState = SignUpInput & {
  options: {
    userAttributes: {
      family_name: string;
      given_name: string;
      'custom:tenant_id': string;
    };
  };
};

type ConfirmSignUpInputState = {
  username: string;
  confirmationCode: string;
};

/**
 * Validates password against Cognito's default password policy:
 * - Minimum length of 8 characters
 * - Contains at least 1 uppercase letter
 * - Contains at least 1 lowercase letter
 * - Contains at least 1 number
 * - Contains at least 1 special character
 */
export const isPasswordValid = (password: string): { valid: boolean; message: string } => {
  if (password.length < 8) {
    return { valid: false, message: 'Password must be at least 8 characters long' };
  }

  if (!/[A-Z]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one uppercase letter' };
  }

  if (!/[a-z]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one lowercase letter' };
  }

  if (!/[0-9]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one number' };
  }

  if (!/[^A-Za-z0-9]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one special character' };
  }

  return { valid: true, message: '' };
};
export default function SignUp() {
  const [loading, setLoading] = useState(false);
  const [signUpInput, setSignUpInput] = useState<SignUpInputState>({
    username: '',
    password: '',
    options: {
      userAttributes: {
        family_name: '',
        given_name: '',
        'custom:tenant_id': ''
      }
    }
  });
  const [confirmSignUpInput, setConfirmSignUpInput] = useState<ConfirmSignUpInputState>({
    username: '',
    confirmationCode: ''
  });
  const [confirmSignUp, setConfirmSignUp] = useState(false);
  const [banner, setBanner] = useState<string | undefined>();
  const [bannerVariant, setBannerVariant] = useState<'error' | 'success'>('error');
  const [showPassword, setShowPassword] = useState(false);
  const [organizations, setOrganizations] = useState<
    { id: string; name: string; tenant: string }[]
  >([]);
  const [disableOrgSelect, setDisableOrgSelect] = useState(true);
  const [acknowledgedConfirmation, setAcknowledgedConfirmation] = useState(false);

  const auth = useAuth();
  const navigate = useNavigate();

  const subdomain = window.location.hostname.split('.')[0];

  const showError = (message?: string) => {
    setBanner(message);
    setBannerVariant('error');
  };

  const showSuccess = (message: string) => {
    setBanner(message);
    setBannerVariant('success');
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const setCleanedError = (rawMessage: string) => {
    const cleanedMessage = rawMessage
      // Strip common Cognito trigger prefixes (PreSignUp/PostConfirmation/etc)
      .replace(/^[\w\s]+failed with error[:\s]*/i, '')
      // Strip generic "WhateverException:" prefixes if present (UserNotFoundException, etc)
      .replace(/^[A-Za-z]+Exception[:\s]*/i, '')
      .trim();
    showError(cleanedMessage);
  };

  /**
   * Fetches organizations and sets the tenant ID if applicable
   */
  const fetchOrganizations = async () => {
    try {
      const result = await listOrganizationsPublic();
      const orgs = (result ?? []) as {
        id: string;
        name: string;
        tenant: string;
        subdomain: string;
      }[];
      setOrganizations(orgs);

      const currentOrg = orgs.find((org) => org.subdomain === subdomain);

      if (currentOrg) {
        setDisableOrgSelect(true);
        setSignUpInput((prevInput) => ({
          ...prevInput,
          options: {
            userAttributes: {
              ...prevInput.options.userAttributes,
              'custom:tenant_id': currentOrg.tenant
            }
          }
        }));
      } else {
        setDisableOrgSelect(false);
      }
    } catch (error) {
      console.warn('Failed to fetch organizations', error);
      showError('Failed to fetch organizations');
      setDisableOrgSelect(false);
    }
  };

  /**
   * Handles the sign up process
   */
  const signUp = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      const normalizedUsername = signUpInput.username.trim().toLowerCase();
      const trimmedPassword = (signUpInput.password ?? '').trim();

      if (!isFormValid()) {
        showError('Please fill out all fields');
        return;
      }

      const passwordValidation = isPasswordValid(trimmedPassword);
      if (!passwordValidation.valid) {
        showError(passwordValidation.message);
        setLoading(false);
        return;
      }

      const result = await auth.signUp({
        ...signUpInput,
        username: normalizedUsername,
        password: trimmedPassword,
        options: {
          userAttributes: {
            ...signUpInput.options.userAttributes,
            email: normalizedUsername
          }
        }
      });

      if (result?.nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        setConfirmSignUp(true);
        setConfirmSignUpInput({ username: normalizedUsername, confirmationCode: '' });
      } else {
        await auth.signIn({
          username: normalizedUsername,
          password: trimmedPassword
        });
        navigate('/');
      }
    } catch (err: any) {
      setCleanedError(err?.message || 'Unable to sign up');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles the confirmation of sign up
   */
  const handleConfirm = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      const normalizedUsername = signUpInput.username.trim().toLowerCase();
      const trimmedPassword = (signUpInput.password ?? '').trim();
      const trimmedConfirmationCode = confirmSignUpInput.confirmationCode.trim();

      await auth.confirmSignUp({
        username: normalizedUsername,
        confirmationCode: trimmedConfirmationCode
      });

      await auth.signIn({
        username: normalizedUsername,
        password: trimmedPassword
      });
      navigate('/dashboard/getting-started');
    } catch (err: any) {
      setCleanedError(err?.message || 'Unable to confirm sign up');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      const normalizedUsername = signUpInput.username.trim().toLowerCase();

      await auth.resendSignUpCode({
        username: normalizedUsername
      });
      showSuccess('Confirmation code resent!');
    } catch (err: any) {
      setCleanedError(err?.message || 'Unable to resend code');
    }
  };

  /**
   * Toggles password visibility
   */
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  /**
   * Validates if all required fields are filled
   */
  const isFormValid = () => {
    return (
      signUpInput.username.trim() !== '' &&
      (signUpInput.password ?? '').trim() !== '' &&
      signUpInput.options.userAttributes.given_name !== '' &&
      signUpInput.options.userAttributes.family_name !== '' &&
      signUpInput.options.userAttributes['custom:tenant_id'] !== '' &&
      acknowledgedConfirmation
    );
  };

  /**
   * Updates the sign up input state
   */
  const updateSignUpInput = (field: string, value: string) => {
    showError();
    setSignUpInput((prevInput) => ({
      ...prevInput,
      [field]: value
    }));
  };

  /**
   * Updates the user attributes in sign up input state
   */
  const updateUserAttribute = (field: string, value: string) => {
    showError();
    setSignUpInput((prevInput) => ({
      ...prevInput,
      options: {
        userAttributes: {
          ...prevInput.options.userAttributes,
          [field]: value
        }
      }
    }));
  };

  return (
    <Container
      title="Create Your Scribl Account"
      subtitle="Connection Starts Here"
      banner={banner}
      bannerVariant={bannerVariant}
      className="relative md:w-600 px-20 py-32 md:px-100 md:py-32 rounded-[12px] bg-white backdrop-blur-xs shadow-xs z-1"
    >
      <form className="w-full flex flex-col space-y-12">
        {confirmSignUp ? (
          <>
            <p className="mt-3 mb-10">
              Please check your email for the confirmation code.
              <p className="mt-3">
                Did not receive the code?{' '}
                <button type="button" className="link link-sm font-semibold" onClick={handleResend}>
                  Resend
                </button>
              </p>
            </p>
            <InputField
              aria-label="Confirmation Code"
              name="confirmationCode"
              label="Confirmation Code"
              value={confirmSignUpInput.confirmationCode}
              onChange={(e) =>
                setConfirmSignUpInput({ ...confirmSignUpInput, confirmationCode: e.target.value })
              }
            />
          </>
        ) : (
          <div className="space-y-12">
            <InputField
              name="firstName"
              label="First Name"
              value={signUpInput.options.userAttributes.given_name}
              onChange={(e) => updateUserAttribute('given_name', e.target.value)}
            />
            <InputField
              name="lastName"
              label="Last Name"
              value={signUpInput.options.userAttributes.family_name}
              onChange={(e) => updateUserAttribute('family_name', e.target.value)}
            />
            {!disableOrgSelect && (
              <OrganizationSelect
                // label mimic via placeholder/structure
                value={signUpInput.options.userAttributes['custom:tenant_id']}
                onChange={(e) => updateUserAttribute('custom:tenant_id', e.target.value)}
                organizations={organizations}
              />
            )}
            <InputField
              name="email"
              label="Email Address"
              value={signUpInput.username}
              onChange={(e) => updateSignUpInput('username', e.target.value)}
            />
            <div className="group">
              <PasswordField
                value={signUpInput.password ?? ''}
                onChange={(e) => updateSignUpInput('password', e.target.value)}
                showPassword={showPassword}
                togglePasswordVisibility={togglePasswordVisibility}
              />
              <div className="hidden group-focus-within:block">
                <PasswordRequirements password={signUpInput.password ?? ''} />
              </div>
            </div>
          </div>
        )}
        <SubmitButton
          title={confirmSignUp ? 'Confirm' : 'Create Account'}
          loading={loading}
          onSubmit={confirmSignUp ? handleConfirm : signUp}
        />
        {!confirmSignUp && (
          <CheckboxField
            label="I have read and agree to the"
            link={{
              text: 'Privacy Policy',
              url: '/privacy-policy'
            }}
            checked={acknowledgedConfirmation}
            onChange={(e) => setAcknowledgedConfirmation(e.target.checked)}
          />
        )}
      </form>
    </Container>
  );
}
